/* eslint-disable @typescript-eslint/no-explicit-any */
import { Dispatch } from 'react';
import { notification } from 'antd';

import { ReportGroupRequestDTO } from './dto/report-group.dto';
import { IAction } from '../root/i-action';
import { IApiService } from '../../services/api-service';
import { IState } from '../root/state.interface';
import {
  IExecutedReport,
  IGetMapReportResponse,
  IGetZonesForMapsResponse,
  IMapControlsState,
  IPreparedReport,
  IReport,
  IReportListValuePair,
  IReportQuickRange,
  IReportSuggestionEntries,
  IReportTree,
} from './i-report';
import { ReportActionType } from './report.enum';
import { IExecuteReport } from './i-execute-report';
import { ReportRequestDTO, ICreateReportResponse } from './dto/report.dto';
import { IColumn } from './i-column';
import { ColumnRequestDTO, ICreateColumnResponse } from './dto/column.dto';
import createHistoryUtil from '../../../utils/create-history';
import { IGetMapReportDefinitionResponse, IGetMapReportDefinitionDefaultResponse } from '../facility/i-facility';
import { facilityBaseUrl } from '../facility/facility.constants';
import { EditMapReportDefinitionDTO } from './dto/map-report-definition.dto';
import { EditMapReportDefinitionNewDTO, ICreateMapDefinitionResponse } from './dto/map-report-definition-new.dto';
import isNumber from '../../../utils/is-number';

export interface IReportActions {
  getReportsList: () => Promise<void>;
  getReports: () => Promise<void>;
  getReport: (id: number) => Promise<void>;
  getExecutedReport: (id: number | string) => Promise<void>;
  copyReport: (id: number) => Promise<{ id: number } | void>;
  prepareReportByCode: (
    code: string,
    findAndSort: { find: string, sort: string },
    type?: string,
    padding?: boolean
  ) => Promise<void>;
  getExportReport: (name: string) => Promise<void>;
  updateExecutedReport: (id: number, data: IExecutedReport) => Promise<void>;
  executeReport: (id: number | string,
    limit?: number,
    skip?: number,
    find?: string | null,
    sort?: string) => Promise<void>;
  createReport: (data: ReportRequestDTO, columns?: IColumn[]) => Promise<void>;
  editReport: (id: number, data: ReportRequestDTO) => Promise<void>;
  deleteReport: (id: number) => Promise<void>;
  createColumn: (data: ColumnRequestDTO) => Promise<void>;
  editColumn: (id: number, data: ColumnRequestDTO) => Promise<void>;
  deleteColumn: (id: number) => Promise<void>;
  getRegexValid: () => Promise<void>;
  createReportGroup: (data: ReportGroupRequestDTO) => Promise<void>;
  editReportGroup: (id: number | null, data: ReportGroupRequestDTO) => Promise<void>;
  deleteReportGroup: (id: number) => Promise<void>;
  getAllColumnsForReport: (collectionName: string) => Promise<void>;
  getCollectionsForReport: () => Promise<void>;
  getReportQuickRanges: () => Promise<void>;
  getListsForReport: (reportId: string | null | undefined) => Promise<void>;
  getReportFieldSuggestions: (
    collection: string,
    fieldName: string,
  ) => Promise<void>;
  mapReportDefinitionByReport(id: number | null): Promise<void>;
  editMapReportDefinition: (
    id: number | null, data: EditMapReportDefinitionDTO
  ) => Promise<void>;
  newMapReportDefinition: (
    data: EditMapReportDefinitionNewDTO,
    dataEditReport: EditMapReportDefinitionDTO,
  ) => Promise<void>;
  getZonesForMap(id: string, limit?: number, skip?: number): Promise<void>;
  getMapReport(
    code: string,
    filter: string | null,
    sort: string | null,
    skip: number,
    pageSize: number
  ): Promise<void>;
  queryReportAndExecute: (
    id: number | string, filterString: string, mergeSorts: string
  ) => Promise<IExecutedReport | null>;
  updateMapControlsState(mapControlsState: IMapControlsState): Promise<void>;
  getBarCodeImage: (url: string) => Promise<Blob>;

  // eslint-disable-next-line
  getTenantAntennasProperty: (group_code: string| undefined) => Promise<any>;
  importAntennas: ({ group_code, data, type }: {
    group_code: string; data: string; type: string;
  }) => Promise<any>;
  // eslint-disable-next-line
  swapReportsPositions: (reports: any[], activeId: number, overId: number) => any[];
  resetExecutedReport: () => void;
}

export class ReportActions implements IReportActions {
  private readonly state: IState;

  private readonly apiService: IApiService;

  protected readonly dispatch: Dispatch<IAction>;

  public constructor(
    state: IState,
    apiService: IApiService,
    dispatch: Dispatch<IAction>,
  ) {
    this.state = state;
    this.apiService = apiService;
    this.dispatch = dispatch;
  }
  // eslint-disable-next-line
  getTenantAntennasProperty = async (group_code: string | undefined): Promise<any> => {
    try {
      // eslint-disable-next-line
      const result = await this.apiService.get<any>({
        url: `/platform-manager/api/antennas/${group_code}`,
      });

      return Promise.resolve(result);
    } catch (error) {
      return Promise.resolve([]);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public importAntennas = async ({ group_code, data, type }: {
    group_code: string;
    data: string;
    type: string;
  }): Promise<any> => {
    try {
      const result = await this.apiService.put({
        url: `/platform-manager/api/antennas/${group_code}`,
        data,
      });
      notification.success({
        message: 'Import saved!',
      });
      return result;
    } catch (error) {
      notification.error({
        message: 'Import failed!',
      });
      throw error;
    }
  };

  public resetExecutedReport = (): void => {
    this.dispatch({ type: ReportActionType.RESET_EXECUTED_REPORT, payload: null });
  };

  public getBarCodeImage = async (url: string): Promise<Blob> => {
    const barCodeManager = await this.apiService.get<Blob>({
      url, responseType: 'blob',
    });
    return barCodeManager;
  };

  public getReportsList = async (): Promise<void> => {
    try {
      this.dispatch({ type: ReportActionType.GET_REPORTS_TREE_REQUEST, payload: null });
      const payload = await this.apiService.get<IReportTree[]>({
        url: '/report-server/api/reports2',
      });
      this.dispatch({ type: ReportActionType.GET_REPORTS_TREE_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: ReportActionType.GET_REPORTS_TREE_FAIL, payload: error });
    }
  };

  public getReports = async (): Promise<void> => {
    try {
      this.dispatch({ type: ReportActionType.GET_REPORTS_REQUEST, payload: null });
      const payload = await this.apiService.get<IReport[]>({
        url: '/report-server/api/reports',
      });
      this.dispatch({ type: ReportActionType.GET_REPORTS_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: ReportActionType.GET_REPORTS_FAIL, payload: error });
    }
  };

  public getReport = async (id: number): Promise<void> => {
    try {
      this.dispatch({ type: ReportActionType.GET_REPORT_REQUEST, payload: null });
      const payload = await this.apiService.get<IReport>({
        url: `/report-server/api/report/${id}`,
      });
      this.dispatch({ type: ReportActionType.GET_REPORT_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: ReportActionType.GET_REPORT_FAIL, payload: error });
    }
  };

  public getCollectionsForReport = async (): Promise<void> => {
    try {
      this.dispatch({ type: ReportActionType.GET_COLLECTIONS_FOR_REPORT_REQUEST, payload: null });
      const payload = await this.apiService.get<IExecutedReport>({
        url: '/report-server/api/collections',
      });
      this.dispatch({ type: ReportActionType.GET_COLLECTIONS_FOR_REPORT_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: ReportActionType.GET_COLLECTIONS_FOR_REPORT_FAIL, payload: error });
    }
  };

  public getExecutedReport = async (id: number | string): Promise<void> => {
    try {
      this.dispatch({ type: ReportActionType.GET_EXECUTED_REPORT_REQUEST, payload: null });
      const reportDefUrl = isNumber(id)
        ? `/report-server/api/report/${id}`
        : `/report-server/api/reportByCode/${id}`;
      const payload = await this.apiService.get<IExecutedReport>({
        url: reportDefUrl,
      });
      this.dispatch({ type: ReportActionType.GET_EXECUTED_REPORT_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: ReportActionType.GET_EXECUTED_REPORT_FAIL, payload: error });
    }
  };

  public queryReportAndExecute = async (
    id: number | string, filterString: string, mergeSorts: string,
  ): Promise<IExecutedReport | null> => {
    try {
      this.dispatch({ type: ReportActionType.GET_EXECUTED_REPORT_REQUEST, payload: null });

      const isNumberId = isNumber(id);
      const reportDefUrl = isNumberId
        ? `/report-server/api/report/${id}`
        : `/report-server/api/reportByCode/${id}`;

      const report = await this.apiService.get<IExecutedReport>({
        url: reportDefUrl,
      });

      const find = filterString || (report.find ? report.find : '');
      const sort = mergeSorts || report.sort || '';

      const reportExecUrl = isNumberId
        ? `/report-server/api/reportExec/${id}`
        : `/report-server/api/reportExecByCode/${id}`;

      const executedReport = await this.apiService.get<IExecuteReport>({
        url: reportExecUrl,
        params: {
          limit: 12,
          skip: 0,
          find,
          sort,
        },
      });
      this.dispatch({ type: ReportActionType.EXECUTE_REPORT_SUCCESS, payload: executedReport });
      this.dispatch({ type: ReportActionType.GET_EXECUTED_REPORT_SUCCESS, payload: report });
      return report;
    } catch (error) {
      this.dispatch({ type: ReportActionType.GET_EXECUTED_REPORT_FAIL, payload: error });
      return null;
    }
  };

  public getAllColumnsForReport = async (collectionName: string): Promise<void> => {
    try {
      if (!collectionName) {
        return;
      }

      this.dispatch({ type: ReportActionType.GET_ALL_REPORT_COLUMNS_REQUEST, payload: null });
      const payload = await this.apiService.get<IExecutedReport>({
        url: `/report-server/api/keys/${collectionName}`,
      });
      this.dispatch({ type: ReportActionType.GET_ALL_REPORT_COLUMNS_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: ReportActionType.GET_ALL_REPORT_COLUMNS_FAIL, payload: error });
    }
  };

  public getExportReport = async (
    name: string,
  ): Promise<void> => {
    try {
      const link = document.createElement('a');
      link.href = `${process.env.REACT_APP_API_HOST || ''}/report-server/api/reportDownloadByName/${name}`;
      link.setAttribute(
        'download',
        `${name}`,
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  public prepareReportByCode = async (
    code: string,
    findAndSort: { find: string, sort: string },
    type?: string,
    padding?: boolean,
  ): Promise<void> => {
    try {
      this.dispatch({ type: ReportActionType.PREPARE_EXPORT_REPORT_REQUEST, payload: null });
      const { find: filter, sort } = findAndSort;
      const payload = await this.apiService.get<IPreparedReport>({
        url: `/report-server/api/reportPrepareByCode/${code}`,
        params: {
          type,
          padding,
          filter,
          sort,
        },
      });
      this.dispatch({ type: ReportActionType.PREPARE_EXPORT_REPORT_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: ReportActionType.PREPARE_EXPORT_REPORT_FAIL, payload: error });
    }
  };

  public copyReport = async (id: number): Promise<{ id: number } | void> => {
    try {
      const payload = await this.apiService.post<null, { id: number }>({
        url: `/report-server/api/report/copy/${id}`,
      });
      return payload;
    } catch (error) {
      return this.dispatch({ type: ReportActionType.COPY_REPORT_FAIL, payload: error });
    }
  };

  public updateExecutedReport = async (id: number, data: IExecutedReport): Promise<void> => {
    try {
      this.dispatch({ type: ReportActionType.UPDATE_EXECUTED_REPORT_REQUEST, payload: null });
      const payload = await this.apiService.put<IExecutedReport, null>({
        url: `/report-server/api/report/${id}`,
        data,
      });
      this.dispatch({ type: ReportActionType.UPDATE_EXECUTED_REPORT_SUCCESS, payload });
      notification.success({
        message: 'Saved!',
      });
    } catch (error) {
      this.dispatch({ type: ReportActionType.UPDATE_EXECUTED_REPORT_FAIL, payload: error });
    }
  };

  public executeReport = async (id: number | string,
    limit?: number,
    skip?: number,
    find?: string | null,
    sort?: string): Promise<void> => {
    try {
      this.dispatch({ type: ReportActionType.EXECUTE_REPORT_REQUEST, payload: null });
      const reportExecUrl = isNumber(id)
        ? `/report-server/api/reportExec/${id}`
        : `/report-server/api/reportExecByCode/${id}`;

      const payload = await this.apiService.get<IExecuteReport>({
        url: reportExecUrl,
        params: {
          limit, skip, find, sort,
        },
      });
      this.dispatch({ type: ReportActionType.EXECUTE_REPORT_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: ReportActionType.EXECUTE_REPORT_FAIL, payload: error });
    }
  };

  public createReport = async (data: ReportRequestDTO, columns?: IColumn[]): Promise<void> => {
    try {
      this.dispatch({ type: ReportActionType.CREATE_REPORT_REQUEST, payload: null });
      const payload = await this.apiService.post<ReportRequestDTO, ICreateReportResponse>({
        url: '/report-server/api/reports',
        data,
      });
      if (columns?.length) {
        columns.map(async (el, index) => {
          const columnWithID = { ...el, report_id: payload.id, order: index + 1 };
          await this.createColumn(columnWithID);
        });
      }
      this.dispatch({ type: ReportActionType.CREATE_REPORT_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: ReportActionType.CREATE_REPORT_FAIL, payload: error });
    }
  };

  public editReport = async (id: number, data: ReportRequestDTO): Promise<void> => {
    try {
      this.dispatch({ type: ReportActionType.EDIT_REPORT_REQUEST, payload: data });
      const payload = await this.apiService.put<ReportRequestDTO, null>({
        url: `/report-server/api/report/${id}`,
        data,
      });
      this.dispatch({ type: ReportActionType.EDIT_REPORT_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: ReportActionType.EDIT_REPORT_FAIL, payload: error });
    }
  };

  public deleteReport = async (id: number): Promise<void> => {
    try {
      this.dispatch({ type: ReportActionType.DELETE_REPORT_REQUEST, payload: null });
      const payload = await this.apiService.delete<null>({
        url: `/report-server/api/report/${id}`,
      });
      this.dispatch({ type: ReportActionType.DELETE_REPORT_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: ReportActionType.DELETE_REPORT_FAIL, payload: error });
    }
  };

  public createColumn = async (data: ColumnRequestDTO): Promise<void> => {
    try {
      this.dispatch({ type: ReportActionType.CREATE_COLUMN_REQUEST, payload: null });
      const payload = await this.apiService.post<ColumnRequestDTO, ICreateColumnResponse>({
        url: '/report-server/api/columns',
        data,
      });
      this.dispatch({ type: ReportActionType.CREATE_COLUMN_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: ReportActionType.CREATE_COLUMN_FAIL, payload: error });
    }
  };

  public editColumn = async (id: number,
    data: ColumnRequestDTO): Promise<void> => {
    try {
      this.dispatch({ type: ReportActionType.EDIT_COLUMN_REQUEST, payload: data });
      const payload = await this.apiService.put<ColumnRequestDTO, null>({
        url: `/report-server/api/column/${id}`,
        data,
      });
      this.dispatch({ type: ReportActionType.EDIT_COLUMN_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: ReportActionType.EDIT_COLUMN_FAIL, payload: error });
    }
  };

  public deleteColumn = async (id: number): Promise<void> => {
    try {
      this.dispatch({ type: ReportActionType.DELETE_COLUMN_REQUEST, payload: null });
      await this.apiService.delete<null>({
        url: `/report-server/api/column/${id}`,
      });
      const payload = this.state.columns.data.filter((el) => el.id !== id);
      this.dispatch({ type: ReportActionType.DELETE_COLUMN_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: ReportActionType.DELETE_COLUMN_FAIL, payload: error });
    }
  };

  public getRegexValid = async (): Promise<void> => {
    try {
      this.dispatch({ type: ReportActionType.GET_REGEX_REQUEST, payload: null });
      // eslint-disable-next-line
      const payload: any = await this.apiService.get<any>({
        url: '/report-server/api/regex',
      });
      // eslint-disable-next-line
      const p = payload && payload.map((r: string) => {
        const a = r.split('\\');
        return a.join('\\');
      });

      this.dispatch({ type: ReportActionType.GET_REGEX_SUCCESS, payload: p });
    } catch (error) {
      this.dispatch({ type: ReportActionType.GET_REGEX_FAIL, payload: error });
    }
  };

  public createReportGroup = async (data: ReportGroupRequestDTO): Promise<void> => {
    try {
      this.dispatch({ type: ReportActionType.CREATE_REPORT_GROUP_REQUEST, payload: null });
      const payload = await this.apiService.post<ReportGroupRequestDTO, ICreateReportResponse>({
        url: '/report-server/crud/reportGroups',
        data,
      });
      this.dispatch({ type: ReportActionType.CREATE_REPORT_GROUP_SUCCESS, payload });
      this.getReportsList();
      createHistoryUtil.goBack();
    } catch (error) {
      this.dispatch({ type: ReportActionType.CREATE_REPORT_GROUP_FAIL, payload: error });
    }
  };

  public editReportGroup = async (
    id: number | null,
    data: ReportGroupRequestDTO,
  ): Promise<void> => {
    try {
      this.dispatch({ type: ReportActionType.EDIT_REPORT_GROUP_REQUEST, payload: null });
      const payload = await this.apiService.put<ReportGroupRequestDTO, ICreateReportResponse>({
        url: `/report-server/crud/reportGroup/${id}`,
        data,
      });
      this.dispatch({ type: ReportActionType.EDIT_REPORT_GROUP_SUCCESS, payload });
      this.getReportsList();
      createHistoryUtil.goBack();
    } catch (error) {
      this.dispatch({ type: ReportActionType.EDIT_REPORT_GROUP_FAIL, payload: error });
    }
  };

  public deleteReportGroup = async (id: number): Promise<void> => {
    try {
      this.dispatch({ type: ReportActionType.DELETE_REPORT_GROUP_REQUEST, payload: null });
      const payload = await this.apiService.delete<null>({
        url: `/report-server/crud/reportGroup/${id}`,
      });
      this.dispatch({ type: ReportActionType.DELETE_REPORT_GROUP_SUCCESS, payload });
      this.getReportsList();
      createHistoryUtil.goBack();
    } catch (error) {
      this.dispatch({ type: ReportActionType.DELETE_REPORT_GROUP_FAIL, payload: error });
    }
  };

  public getReportQuickRanges = async (): Promise<void> => {
    try {
      this.dispatch({ type: ReportActionType.GET_REPORT_QUICK_RANGES_REQUEST, payload: null });
      const payload = await this.apiService.get<IReportQuickRange[]>({
        url: '/report-server/api/quickRanges',
      });
      this.dispatch({ type: ReportActionType.GET_REPORT_QUICK_RANGES_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: ReportActionType.GET_REPORT_QUICK_RANGES_FAIL, payload: error });
    }
  };

  public getListsForReport = async (reportId: string | null | undefined): Promise<void> => {
    try {
      this.dispatch({ type: ReportActionType.GET_LISTS_FOR_REPORT_REQUEST, payload: null });

      const promises = [this.apiService.get<string[]>({
        url: '/report-server/api/regex',
      }), this.apiService.get<IReportQuickRange[]>({
        url: '/report-server/api/quickRanges',
      }), this.apiService.get<IReportListValuePair[]>({
        url: '/report-server/api/filterOperatorList',
      })];

      if (reportId) {
        promises.push(this.apiService.get<IReportListValuePair[]>({
          url: `/report-server/api/filterFields/${reportId}`,
        }));
      }

      const data: PromiseSettledResult<
      string[] | IReportQuickRange[] | IReportListValuePair[]
      >[] = await Promise.allSettled(promises);

      const defaultData = (result: PromiseSettledResult<
      string[] | IReportQuickRange[] | IReportListValuePair[]
      >, defaultReturn = []) => (result.status === 'fulfilled' ? result.value : defaultReturn);

      const regex = defaultData(data[0]);
      regex.shift();

      this.dispatch({
        type: ReportActionType.GET_LISTS_FOR_REPORT_SUCCESS,
        payload: {
          regex,
          quickRanges: defaultData(data[1]),
          operators: [
            {
              text: 'begins with',
              value: '$begins',
            },
            {
              text: 'ends with',
              value: '$ends',
            },
            {
              text: 'contains',
              value: '$contains',
            },
            ...defaultData(data[2]),
          ],
          filterFields: reportId ? defaultData(data[3]) : [],
        },
      });
    } catch (e) {
      this.dispatch({ type: ReportActionType.GET_LISTS_FOR_REPORT_FAIL, payload: e });
    }
  };

  public getReportFieldSuggestions = async (
    collection: string,
    fieldName: string,
  ): Promise<void> => {
    try {
      this.dispatch({ type: ReportActionType.GET_REPORT_SUGGESTIONS_REQUEST, payload: null });

      const data = await this.apiService.get<IReportListValuePair>({
        url: `/report-server/api/filterValueSuggestions/${collection}/${fieldName}`,
      });

      this.dispatch(
        {
          type: ReportActionType.GET_REPORT_SUGGESTIONS_SUCCESS,
          payload: {
            fieldName,
            data,
          },
        },
      );
    } catch (e) {
      this.dispatch({ type: ReportActionType.GET_REPORT_SUGGESTIONS_FAIL, payload: e });
    }
  };

  public mapReportDefinitionByReport = async (id: number | null): Promise<void> => {
    try {
      this.dispatch(
        { type: ReportActionType.GET_MAPS_REPORTS_DEFINITION_REQUEST, payload: null },
      );
      const payload = await this.apiService.get<IGetMapReportDefinitionResponse>({
        url: `/report-server/api/mapReportDefinitionByReport/${id}`,
      });
      this.dispatch({ type: ReportActionType.GET_MAPS_REPORTS_DEFINITION_SUCCESS, payload });
    } catch (err) {
      try {
        this.dispatch(
          { type: ReportActionType.GET_MAPS_REPORTS_DEFINITION_DEFAULT_REQUEST, payload: null },
        );
        const payload = await this.apiService.get<IGetMapReportDefinitionDefaultResponse>({
          url: `/report-server/api/mapReportDefinitionDefault/${id}`,
        });
        this.dispatch(
          { type: ReportActionType.GET_MAPS_REPORTS_DEFINITION_DEFAULT_SUCCESS, payload },
        );
      } catch (err2) {
        this.dispatch(
          { type: ReportActionType.GET_MAPS_REPORTS_DEFINITION_DEFAULT_FAIL, payload: err },
        );
      }
    }
  };

  public editMapReportDefinition = async (
    id: number | null,
    data: EditMapReportDefinitionDTO,
  ): Promise<void> => {
    try {
      this.dispatch({ type: ReportActionType.EDIT_MAP_REPORT_DEFINITION_REQUEST, payload: data });
      const payload = await this.apiService.put<EditMapReportDefinitionDTO, null>({
        url: `/report-server/api/mapReportDefinition/${id}`,
        data,
      });
      notification.success({
        message: 'Controls were saved.',
        description:
          'The constrols state was saved for next session',
        getContainer: () => document.getElementById('map') as HTMLElement,

      });
      this.dispatch({ type: ReportActionType.EDIT_MAP_REPORT_DEFINITION_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: ReportActionType.EDIT_MAP_REPORT_DEFINITION_FAIL, payload: error });
    }
  };

  public newMapReportDefinition = async (
    data: EditMapReportDefinitionNewDTO,
    dataEditReport: EditMapReportDefinitionDTO,
  ): Promise<void> => {
    try {
      this.dispatch({ type: ReportActionType.EDIT_MAP_REPORT_DEFINITION_REQUEST, payload: data });
      // eslint-disable-next-line max-len
      const payload = await this.apiService.post<EditMapReportDefinitionNewDTO, ICreateMapDefinitionResponse>({
        url: '/report-server/api/mapReportDefinitions',
        data,
      });
      if (data.reportdef_id) {
        // eslint-disable-next-line no-param-reassign
        dataEditReport.id = payload.id;
        // eslint-disable-next-line no-param-reassign
        dataEditReport.reportdef_id = data.reportdef_id;
        // eslint-disable-next-line no-param-reassign
        dataEditReport.name = data.name;
        this.editMapReportDefinition(payload.id, dataEditReport);
      }
      this.dispatch({ type: ReportActionType.EDIT_MAP_REPORT_DEFINITION_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: ReportActionType.EDIT_MAP_REPORT_DEFINITION_FAIL, payload: error });
    }
  };

  public getZonesForMap = async (id: string, limit = -1, skip = 0): Promise<void> => {
    try {
      this.dispatch({ type: ReportActionType.GET_ZONES_LIST_FOR_MAPS_REQUEST, payload: null });
      const payload = await this.apiService.get<IGetZonesForMapsResponse>({
        url: `${facilityBaseUrl}zones`,
        params: {
          map: true,
          limit,
          skip,
          group_id: id,
        },
      });

      this.dispatch({ type: ReportActionType.GET_ZONES_FOR_MAPS_LIST_SUCCESS, payload });
    } catch (err) {
      this.dispatch({ type: ReportActionType.GET_ZONES_FOR_MAPS_LIST_FAIL, payload: err });
    }
  };

  public getMapReport = async (
    code: string,
    filter: string,
    sort: string,
    skip: number,
    pageSize: number,
  ): Promise<void> => {
    try {
      this.dispatch({ type: ReportActionType.GET_MAP_REPORT_REQUEST, payload: null });
      const params: IReportSuggestionEntries = {};

      // eslint-disable-next-line
      filter && (params.filter = filter);
      // eslint-disable-next-line
      sort && (params.sort = sort);
      // eslint-disable-next-line
      skip && (params.skip = skip);
      // eslint-disable-next-line
      pageSize && (params.limit = pageSize);

      const payload = await this.apiService.get<IGetMapReportResponse>({
        url: `/report-server/api/mapReportExecByCode/${code}`,
        params,
      });
      this.dispatch({ type: ReportActionType.GET_MAP_REPORT_SUCCESS, payload });
    } catch (err) {
      this.dispatch({ type: ReportActionType.GET_MAP_REPORT_FAIL, payload: err });
    }
  };

  public updateMapControlsState = async (mapControlsState: IMapControlsState): Promise<void> => {
    try {
      this.dispatch({ type: ReportActionType.MAP_CONTROLS_STATE, payload: mapControlsState });
    } catch (err) {
      this.dispatch({ type: ReportActionType.MAP_CONTROLS_STATE_FAIL, payload: err });
    }
  };

  swapReportsPositions = (
    // eslint-disable-next-line
    reports: any[],
    activeId: number, overId: number,
    // eslint-disable-next-line
  ): any[] => {
    const oldIndex = reports.findIndex((item) => item.id === activeId);
    const newIndex = reports.findIndex((item) => item.id === overId);

    const reordered = [...reports];

    const old = reordered[oldIndex];
    reordered[oldIndex] = reordered[newIndex];
    reordered[newIndex] = old;

    try {
      // eslint-disable-next-line
      this.apiService.put<any[], any>({
        url: '/report-server/api/reports/order',
        data: reordered.map((item) => item.id),
      });
    } catch {
      // just silently ignore an error
    }

    return reordered;
  };
}
